import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Power, Image, Trash2 } from 'lucide-react';
import { supabase } from '../services/supabase';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';

interface Product {
  id: string;
  name: string;
  description: string | null;
  price_cents: number;
  stock: number | null;
  category: string | null;
  images: string[] | null;
  sizes: string[] | null;
  colors: string[] | null;
  active: boolean;
  fulfillment_type: string;
  validity_days: number | null;
  components: unknown | null;
}
interface ProductForm {
  name: string;
  description: string;
  price_cents: number;
  stock: number;
  category: string;
  sizes: string;
  colors: string;
  fulfillment_type: string;
  validity_days: number;
  service_count: number;
  bundle_video_count: number;
  bundle_session_count: number;
  // Bundle GYM+Tecnificación (mig 135): meses de tecnificación (= nº de tokens
  // de suscripción) + días de validez del acceso GYM del tramo.
  gym_tecnif_months: number;
  gym_tecnif_validity_days: number;
}
const FORM_EMPTY: ProductForm = {
  name: '',
  description: '',
  price_cents: 3500,
  stock: 0,
  category: 'camiseta',
  sizes: 'XS,S,M,L,XL,XXL',
  colors: '',
  fulfillment_type: 'physical_stock',
  validity_days: 90,
  service_count: 1,
  bundle_video_count: 1,
  bundle_session_count: 1,
  gym_tecnif_months: 3,
  gym_tecnif_validity_days: 90,
};
const CAT_LABELS: Record<string, string> = {
  equipacion: 'Equipación',
  camiseta: 'Camiseta',
  accesorio: 'Accesorio',
};
const FULFILLMENT_LABELS: Record<string, string> = {
  physical_stock: 'Producto físico',
  membership: 'Matrícula',
  subscription: 'Suscripción',
  service: 'Servicio (análisis de vídeo)',
  bundle: 'Pack combinado',
  gym_tecnif_subscription: 'Preparación Física + Tecnificación',
  individual_tecnif_subscription: 'Tecnificación individual',
};

// §8 rediseño huecos — NAMING de tienda por PRESENTACIÓN (sin migración): el admin
// elige una CATEGORÍA; "Suscripción" agrupa las variantes Grupal / Individual /
// GYM+Tecnificación. Por debajo, cada variante mapea a su fulfillment_type plano
// existente (el webhook y las tablas NO cambian). Agrupar ≠ fusionar.
const SUBSCRIPTION_FULFILLMENTS = [
  'subscription',
  'individual_tecnif_subscription',
  'gym_tecnif_subscription',
] as const;
const CATEGORY_LABELS: Record<string, string> = {
  suscripcion: 'Suscripción',
  physical_stock: 'Producto físico',
  membership: 'Matrícula',
  service: 'Servicio (análisis de vídeo)',
  bundle: 'Pack combinado',
};
// Categoría de presentación → fulfillment_type por defecto al elegirla.
const CATEGORY_DEFAULT_FULFILLMENT: Record<string, string> = {
  suscripcion: 'subscription',
  physical_stock: 'physical_stock',
  membership: 'membership',
  service: 'service',
  bundle: 'bundle',
};
const SUBSCRIPTION_VARIANT_LABELS: Record<string, string> = {
  subscription: 'Grupal',
  individual_tecnif_subscription: 'Individual',
  gym_tecnif_subscription: 'Preparación Física + Tecnificación',
};
// fulfillment_type (lo que se persiste) → categoría de presentación.
function categoryOfFulfillment(ft: string): string {
  return (SUBSCRIPTION_FULFILLMENTS as readonly string[]).includes(ft) ? 'suscripcion' : ft;
}

// Descompone el JSON de componentes de un bundle 'gym_tecnif_subscription' en
// los meses de tecnificación (componente `subscription`) y los días de validez
// del acceso GYM (componente `gym_access`). Defaults de creación si falta algo.
// ============================================================
// COMBINADOS v2 — REFERENCIAS, NO DESCRIPTORES
// ============================================================
// El formato viejo DESCRIBE lo que un producto concede:
//   [{"type":"service","subtype":"video_analysis","count":4}, …]
// El v2 dice qué PRODUCTOS incluye el combinado:
//   {"v":2,"items":[{"kind":"product","id":"…","quantity":1}, …]}
//
// ⚠️ `kind` es obligatorio y no es ceremonia: la preparación física son
// `session_packs`, NO `products`. Sin él, un combinado con PF no es
// representable.
//
// Los dos formatos conviven y NO migra ninguna fila. El discriminador es el que
// el resto del código ya usa —`Array.isArray` → viejo— así que una fila v2 es
// invisible para `fulfillServiceOrBundle`, `gymTecnifOf`, `serviceCountOf` y
// `bundleCountsOf`, que empiezan todos por ahí y devuelven un default.
export type ItemV2 = { kind: 'product' | 'session_pack'; id: string; quantity: number };

export function esComponentesV2(components: unknown): boolean {
  return (
    typeof components === 'object' &&
    components !== null &&
    !Array.isArray(components) &&
    (components as { v?: unknown }).v === 2
  );
}

export function itemsDeV2(components: unknown): ItemV2[] {
  if (!esComponentesV2(components)) return [];
  const items = (components as { items?: unknown }).items;
  if (!Array.isArray(items)) return [];
  return items.map((raw) => {
    const it = raw as Partial<ItemV2>;
    return {
      kind: it.kind === 'session_pack' ? 'session_pack' : 'product',
      id: String(it.id ?? ''),
      quantity: Math.max(1, Number(it.quantity) || 1),
    };
  });
}

// Un combinado del formato viejo se sigue editando con el formulario de dos
// campos que ya había. No se convierte a v2: sus `session_credit:individual`
// son créditos sueltos que no corresponden a ningún bono del catálogo, así que
// no son expresables como referencias.
// Las mismas reglas que revalida el trigger de la mig 215. Aquí para poder
// decirlas en cristiano antes de guardar; allí porque el panel no es autoridad.
// Devuelve el motivo del rechazo, o null si está bien.
export function validarCombinadoV2(
  items: ItemV2[],
  productos: { id: string; fulfillment_type: string }[]
): string | null {
  if (items.length < 2) {
    return 'Un combinado necesita al menos 2 componentes.';
  }
  if (items.some((i) => !i.id)) {
    return 'Hay un componente sin elegir.';
  }
  const ftDe = (id: string) => productos.find((p) => p.id === id)?.fulfillment_type;

  // REGLA DURA: un combinado dentro de otro abre recursión, ciclos y un
  // "¿qué contiene esto de verdad?" que nadie quiere depurar cuando falle un
  // cobro.
  const anidado = items.find((i) => i.kind === 'product' && ftDe(i.id) === 'bundle');
  if (anidado) {
    return 'Un combinado no puede contener otro combinado.';
  }

  // Cobrar dos matrículas en la misma compra no significa nada: la segunda solo
  // alargaría la fecha sobre la primera.
  const matriculas = items.filter((i) => i.kind === 'product' && ftDe(i.id) === 'membership');
  if (matriculas.length > 1) {
    return 'Un combinado lleva como mucho una matrícula.';
  }

  if (items.some((i) => i.quantity < 1)) {
    return 'La cantidad de cada componente tiene que ser al menos 1.';
  }
  return null;
}

export function esCombinadoLegado(p: { fulfillment_type: string; components: unknown }): boolean {
  return p.fulfillment_type === 'bundle' && Array.isArray(p.components);
}

function gymTecnifOf(components: unknown): { months: number; validityDays: number } {
  if (!Array.isArray(components)) return { months: 3, validityDays: 90 };
  const sub = components.find((c) => (c as { type?: string })?.type === 'subscription') as
    | { months?: number }
    | undefined;
  const gym = components.find((c) => (c as { type?: string })?.type === 'gym_access') as
    | { validity_days?: number }
    | undefined;
  return { months: sub?.months ?? 3, validityDays: gym?.validity_days ?? 90 };
}

// Extrae el `count` del componente de servicio de un producto 'service'.
function serviceCountOf(components: unknown): number {
  if (!Array.isArray(components)) return 1;
  const svc = components.find((c) => (c as { type?: string })?.type === 'service') as
    | { count?: number }
    | undefined;
  return svc?.count ?? 1;
}

// Descompone el JSON de componentes de un 'bundle' en las dos cantidades que
// edita el admin (vídeo análisis + sesiones individuales). Si el JSON es
// inválido o está vacío, ambas caen a 1 (default de creación). Si es válido
// pero falta un componente, su cantidad es 0 (el pack no lo concede).
function bundleCountsOf(components: unknown): { video: number; sessions: number } {
  if (!Array.isArray(components) || components.length === 0) return { video: 1, sessions: 1 };
  const svc = components.find((c) => (c as { type?: string })?.type === 'service') as
    | { count?: number }
    | undefined;
  const sess = components.find((c) => (c as { type?: string })?.type === 'session_credit') as
    | { count?: number }
    | undefined;
  return { video: svc?.count ?? 0, sessions: sess?.count ?? 0 };
}

interface SessionPack {
  id: string;
  name: string;
  price_cents: number;
  active: boolean;
}

export default function TiendaPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Product | null>(null);
  const [form, setForm] = useState<ProductForm>(FORM_EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<'activos' | 'inactivos'>('activos');
  const [confirmDelete, setConfirmDelete] = useState<Product | null>(null);
  // Componentes del combinado v2 en edición, y el catálogo de bonos para el
  // desplegable (los productos ya están en `products`).
  const [itemsV2, setItemsV2] = useState<ItemV2[]>([]);
  const [bonos, setBonos] = useState<SessionPack[]>([]);
  // Con el catálogo ilegible la página dice "Sin productos activos." y los
  // contadores se quedan a (0). Lo peor no es la frase: es que desde ahí se
  // pulsa "Nuevo Producto" y se crea uno que ya existe, y entonces la tienda
  // del alumno tiene la misma camiseta dos veces. Por eso el alta se bloquea
  // mientras no se sepa qué hay.
  const [errorCarga, setErrorCarga] = useState('');

  useEffect(() => {
    loadProducts();
  }, []);

  async function loadProducts() {
    const { data, error } = await supabase.from('products').select('*').order('name');
    setProducts(data ?? []);
    // Los bonos entran en el desplegable de componentes: la preparación física
    // vive aquí, no en `products`. Si NO se leen, el combinado se guardaría sin
    // el componente que le falta, así que su fallo cuenta igual que el otro.
    const { data: packs, error: errPacks } = await supabase
      .from('session_packs')
      .select('id, name, price_cents, active')
      .order('name');
    setBonos((packs ?? []) as SessionPack[]);
    const falla = error ?? errPacks;
    setErrorCarga(
      falla ? `No se pudo cargar el catálogo${error ? '' : ' de bonos'}: ${falla.message}` : ''
    );
    setLoading(false);
  }

  function openCreate() {
    setEditTarget(null);
    setItemsV2([]);
    setForm(FORM_EMPTY);
    setImages([]);
    setUploadError('');
    setSaveError('');
    setModal(true);
  }
  function openEdit(p: Product) {
    setEditTarget(p);
    setItemsV2(itemsDeV2(p.components));
    const bundle = bundleCountsOf(p.components);
    const gymTecnif = gymTecnifOf(p.components);
    setForm({
      name: p.name,
      description: p.description ?? '',
      price_cents: p.price_cents,
      stock: p.stock ?? 0,
      category: p.category ?? 'camiseta',
      sizes: (p.sizes ?? []).join(','),
      colors: (p.colors ?? []).join(','),
      fulfillment_type: p.fulfillment_type ?? 'physical_stock',
      validity_days: p.validity_days ?? 90,
      service_count: serviceCountOf(p.components),
      bundle_video_count: bundle.video,
      bundle_session_count: bundle.sessions,
      gym_tecnif_months: gymTecnif.months,
      gym_tecnif_validity_days: gymTecnif.validityDays,
    });
    setImages(p.images ?? []);
    setSaveError('');
    setModal(true);
  }

  async function uploadImage(file: File): Promise<string | null> {
    const ext = file.name.split('.').pop();
    const path = `products/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('vertex-assets').upload(path, file);
    if (error) {
      setUploadError(
        `Error al subir "${file.name}": ${error.message}. Asegúrate de que el bucket "vertex-assets" existe y es público en Supabase Storage.`
      );
      return null;
    }
    const { data } = supabase.storage.from('vertex-assets').getPublicUrl(path);
    return data.publicUrl;
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    setUploadError('');
    setUploading(true);
    const urls = await Promise.all(files.map(uploadImage));
    setImages((prev) => [...prev, ...(urls.filter(Boolean) as string[])]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  // Lo que puede entrar en un combinado: cualquier producto MENOS otro
  // combinado (regla dura). Los inactivos sí salen —con su etiqueta— porque
  // montar la oferta antes de activar sus piezas es un orden legítimo.
  const productosComponibles = products.filter(
    (p) => p.fulfillment_type !== 'bundle' && p.id !== editTarget?.id
  );

  const precioDe = (it: ItemV2) =>
    it.kind === 'session_pack'
      ? (bonos.find((b) => b.id === it.id)?.price_cents ?? 0)
      : (products.find((p) => p.id === it.id)?.price_cents ?? 0);

  const sumaDeLasPartes = itemsV2.reduce((acc, it) => acc + precioDe(it) * it.quantity, 0);

  const componentesInactivos = itemsV2
    .map((it) =>
      it.kind === 'session_pack'
        ? bonos.find((b) => b.id === it.id)
        : products.find((p) => p.id === it.id)
    )
    .filter((x): x is NonNullable<typeof x> => !!x && !x.active)
    .map((x) => x.name);

  // Un combinado ya existente en el formato viejo. Ni el formulario ni el
  // guardado son los mismos.
  const editandoLegado = !!editTarget && esCombinadoLegado(editTarget);

  async function handleSave() {
    setSaveError('');
    const isPhysical = form.fulfillment_type === 'physical_stock';
    const isService = form.fulfillment_type === 'service';
    const isBundle = form.fulfillment_type === 'bundle';
    const isGymTecnif = form.fulfillment_type === 'gym_tecnif_subscription';
    const esBundleV2 = isBundle && !editandoLegado;

    // components: lo construimos desde las cantidades estructuradas que edita el
    // admin. El webhook itera la lista para conceder créditos.
    let components: unknown | null = null;
    if (isService) {
      if (form.service_count < 1) {
        setSaveError('La cantidad de vídeo análisis debe ser al menos 1.');
        return;
      }
      components = [{ type: 'service', subtype: 'video_analysis', count: form.service_count }];
    } else if (isBundle && editandoLegado) {
      // Combinado del formato VIEJO: se sigue editando tal cual. No se
      // convierte a v2 — sus créditos sueltos no son expresables como
      // referencias a nada del catálogo.
      const comps: unknown[] = [];
      if (form.bundle_video_count > 0) {
        comps.push({
          type: 'service',
          subtype: 'video_analysis',
          count: form.bundle_video_count,
        });
      }
      if (form.bundle_session_count > 0) {
        comps.push({
          type: 'session_credit',
          credit_type: 'individual',
          count: form.bundle_session_count,
        });
      }
      if (comps.length === 0) {
        setSaveError('Un pack combinado necesita al menos un componente con cantidad mayor que 0.');
        return;
      }
      components = comps;
    } else if (esBundleV2) {
      // Combinado v2. Estas validaciones son las MISMAS que revalida el trigger
      // de la mig 215: aquí para decirlo en cristiano y allí porque el panel no
      // es autoridad —la policy de `products` es FOR ALL para dirección—.
      const error = validarCombinadoV2(itemsV2, products);
      if (error) {
        setSaveError(error);
        return;
      }
      components = { v: 2, items: itemsV2 };
    } else if (isGymTecnif) {
      // Bundle GYM+Tecnificación (mig 135): N tokens de suscripción + 1 acceso GYM.
      if (form.gym_tecnif_months < 1) {
        setSaveError('Los meses de tecnificación deben ser al menos 1.');
        return;
      }
      if (form.gym_tecnif_validity_days < 1) {
        setSaveError('La validez del acceso Preparación Física debe ser al menos 1 día.');
        return;
      }
      components = [
        { type: 'gym_access', validity_days: form.gym_tecnif_validity_days },
        { type: 'subscription', months: form.gym_tecnif_months },
      ];
    }

    setSaving(true);
    const payload = {
      name: form.name,
      description: form.description || null,
      price_cents: form.price_cents,
      fulfillment_type: form.fulfillment_type,
      // Para matrícula/suscripción/servicio/bundle no aplica la categoría.
      category: isPhysical ? form.category : null,
      // Caducidad de los créditos generados (service/bundle). NULL = sin caducidad.
      validity_days: isService || isBundle ? form.validity_days || null : null,
      components,
      images: images.length ? images : null,
    };
    // stock/sizes/colors NO están en `payload` a propósito. Desde la mig 214
    // son el ESPEJO de product_variants, no el dato:
    //
    //   · al EDITAR no se mandan. Escribirlos no crearía ningún movimiento y
    //     el siguiente cambio de variante los revertiría — el operador vería
    //     su edición deshacerse sola. Se gestionan en /inventario, que es
    //     donde se pide el motivo.
    //   · al CREAR sí: ahí son la declaración de los ejes del producto, y el
    //     trigger trg_products_ensure_variants los convierte en sus variantes.
    //
    // stock va NULL para lo no físico (mig 123), no el placeholder 0.
    // `saveError` ya existía y se pinta en el modal (lo usa la validación),
    // pero el guardado no lo tocaba: cerraba y recargaba pasara lo que pasara.
    if (editTarget) {
      const { error } = await supabase.from('products').update(payload).eq('id', editTarget.id);
      if (error) {
        setSaveError(`No se pudo guardar el producto: ${error.message}`);
        setSaving(false);
        return;
      }
    } else {
      const listaDe = (crudo: string) =>
        isPhysical && crudo
          ? crudo
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : null;
      const { error } = await supabase.from('products').insert({
        ...payload,
        // ⚠️ UN COMBINADO v2 NACE APAGADO, Y NO ES UNA PREFERENCIA.
        // El webhook todavía no sabe leer el formato v2: sus decodificadores
        // empiezan por `Array.isArray` y devuelven un default, así que un
        // combinado v2 a la venta se cobraría ENTERO y no concedería NADA.
        // Se activa cuando F2 y F3 estén desplegadas y verificadas, no antes.
        // El resto de productos conservan el default de la tabla (activo).
        ...(esBundleV2 ? { active: false } : {}),
        stock: isPhysical ? form.stock : null,
        sizes: listaDe(form.sizes),
        colors: listaDe(form.colors),
      });
      if (error) {
        setSaveError(`No se pudo crear el producto: ${error.message}`);
        setSaving(false);
        return;
      }
    }
    await loadProducts();
    setModal(false);
    setSaving(false);
  }

  async function toggleProduct(p: Product) {
    const { error } = await supabase.from('products').update({ active: !p.active }).eq('id', p.id);
    // El error se comprueba ANTES de tocar el estado: pintar el producto como
    // desactivado cuando la base ha dicho que no es peor que no pintar nada,
    // porque el cambio falso sobrevive hasta la siguiente recarga.
    if (error) {
      alert(`No se pudo ${p.active ? 'desactivar' : 'activar'} "${p.name}": ${error.message}`);
      return;
    }
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: !x.active } : x)));
  }

  async function deleteProduct(p: Product) {
    const { error } = await supabase.from('products').delete().eq('id', p.id);
    if (error) {
      alert(`No se pudo eliminar: ${error.message}`);
      return;
    }
    setProducts((prev) => prev.filter((x) => x.id !== p.id));
    setConfirmDelete(null);
  }

  const filteredProducts = products.filter((p) => (tab === 'activos' ? p.active : !p.active));
  const activeCount = products.filter((p) => p.active).length;
  const inactiveCount = products.filter((p) => !p.active).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-bg-elevated p-1 rounded-lg">
          <button
            onClick={() => setTab('activos')}
            className={`px-4 py-1.5 rounded text-sm font-inter transition-colors ${tab === 'activos' ? 'bg-gold-primary text-bg-primary' : 'text-text-secondary hover:text-white'}`}
          >
            Activos <span className="opacity-70">({errorCarga ? '—' : activeCount})</span>
          </button>
          <button
            onClick={() => setTab('inactivos')}
            className={`px-4 py-1.5 rounded text-sm font-inter transition-colors ${tab === 'inactivos' ? 'bg-gold-primary text-bg-primary' : 'text-text-secondary hover:text-white'}`}
          >
            Inactivos <span className="opacity-70">({errorCarga ? '—' : inactiveCount})</span>
          </button>
        </div>
        <button
          onClick={openCreate}
          disabled={errorCarga !== ''}
          title={
            errorCarga
              ? 'No se ha podido leer el catálogo: crear ahora puede duplicar un producto que ya existe.'
              : undefined
          }
          className="btn-gold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="tienda-nuevo-producto"
        >
          <Plus size={16} /> Nuevo Producto
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center h-48 items-center">
          <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
        </div>
      ) : errorCarga ? (
        <div className="card text-center py-12" data-testid="tienda-ilegible">
          <p className="text-error text-sm">{errorCarga}</p>
          <p className="text-text-secondary text-sm mt-2">
            No des el catálogo por vacío: la consulta ha fallado. Crear un producto ahora duplicaría
            el que ya esté dado de alta, y el alumno vería los dos en la tienda.
          </p>
          <button
            className="btn-secondary mt-4"
            onClick={loadProducts}
            data-testid="tienda-reintentar"
          >
            Reintentar
          </button>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-text-secondary text-sm">
            {tab === 'activos' ? 'Sin productos activos.' : 'Sin productos inactivos.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((p) => (
            <div key={p.id} className={`card p-0 overflow-hidden ${!p.active ? 'opacity-60' : ''}`}>
              <div className="h-40 bg-bg-elevated flex items-center justify-center relative">
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <Image size={32} className="text-bg-border" />
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    onClick={() => openEdit(p)}
                    title="Editar"
                    className="w-7 h-7 bg-bg-surface/80 rounded flex items-center justify-center text-text-secondary hover:text-gold-primary"
                  >
                    <Edit size={12} />
                  </button>
                  <button
                    onClick={() => toggleProduct(p)}
                    title={p.active ? 'Desactivar' : 'Activar'}
                    className={`w-7 h-7 bg-bg-surface/80 rounded flex items-center justify-center text-text-secondary ${p.active ? 'hover:text-warning' : 'hover:text-success'}`}
                  >
                    <Power size={12} />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(p)}
                    title="Eliminar"
                    className="w-7 h-7 bg-bg-surface/80 rounded flex items-center justify-center text-text-secondary hover:text-error"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                {!p.active && (
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-error/80 rounded text-[10px] text-white font-inter">
                    Inactivo
                  </div>
                )}
              </div>
              <div className="p-3">
                <p className="font-inter font-medium text-white text-sm truncate">{p.name}</p>
                <p className="text-text-secondary text-xs">
                  {p.fulfillment_type === 'physical_stock'
                    ? p.category
                      ? CAT_LABELS[p.category]
                      : '—'
                    : (FULFILLMENT_LABELS[p.fulfillment_type] ?? p.fulfillment_type)}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <p className="font-oswald text-gold-primary text-lg">
                    {(p.price_cents / 100).toFixed(2)}€
                  </p>
                  {p.fulfillment_type === 'physical_stock' ? (
                    <p className="text-text-secondary text-xs">Stock: {p.stock}</p>
                  ) : (
                    <p className="text-text-secondary text-xs">
                      {p.fulfillment_type === 'membership'
                        ? 'Vigencia 31/07'
                        : p.fulfillment_type === 'subscription'
                          ? 'Mensual'
                          : p.validity_days
                            ? `Validez ${p.validity_days}d`
                            : 'Sin caducidad'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Confirm
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteProduct(confirmDelete)}
        title="Eliminar producto"
        message={`¿Borrar "${confirmDelete?.name}" definitivamente? Los pedidos que ya lo contenían no se verán afectados (guardan el nombre y precio históricos). Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        danger
      />

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editTarget ? 'Editar Producto' : 'Nuevo Producto'}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input
              className="input"
              data-testid="product-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Categoría</label>
              <select
                className="input"
                data-testid="select-category"
                value={categoryOfFulfillment(form.fulfillment_type)}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    fulfillment_type: CATEGORY_DEFAULT_FULFILLMENT[e.target.value],
                  }))
                }
                disabled={!!editTarget}
              >
                {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Precio (€)</label>
              <input
                type="number"
                step="0.01"
                className="input"
                value={(form.price_cents / 100).toFixed(2)}
                onChange={(e) =>
                  setForm((f) => ({ ...f, price_cents: Math.round(+e.target.value * 100) }))
                }
              />
            </div>
          </div>
          {categoryOfFulfillment(form.fulfillment_type) === 'suscripcion' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Variante</label>
                <select
                  className="input"
                  data-testid="select-subscription-variant"
                  value={form.fulfillment_type}
                  onChange={(e) => setForm((f) => ({ ...f, fulfillment_type: e.target.value }))}
                  disabled={!!editTarget}
                >
                  {SUBSCRIPTION_FULFILLMENTS.map((v) => (
                    <option key={v} value={v}>
                      {SUBSCRIPTION_VARIANT_LABELS[v]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {form.fulfillment_type === 'membership' && (
            <p className="text-xs text-text-secondary bg-bg-elevated rounded p-2">
              Matrícula: vigencia hasta el 31/07 de la próxima temporada. No requiere stock ni
              variantes.
            </p>
          )}
          {form.fulfillment_type === 'subscription' && (
            <p
              className="text-xs text-text-secondary bg-bg-elevated rounded p-2"
              data-testid="subscription-note"
            >
              Suscripción mensual: precio uniforme. El alumno la compra y luego la activa eligiendo
              mes, categoría, día y hora. No requiere stock ni variantes.
            </p>
          )}
          {form.fulfillment_type === 'individual_tecnif_subscription' && (
            <p
              className="text-xs text-text-secondary bg-bg-elevated rounded p-2"
              data-testid="individual-tecnif-note"
            >
              Tecnificación individual (1-a-1): precio uniforme. El alumno la compra y luego
              solicita el inicio; un administrador crea la serie de 4 sesiones en su slot semanal.
              No requiere stock ni variantes.
            </p>
          )}
          {form.fulfillment_type === 'service' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Cantidad de vídeo análisis</label>
                <input
                  type="number"
                  min={1}
                  className="input"
                  data-testid="service-video-count"
                  value={form.service_count}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, service_count: Math.max(1, +e.target.value) }))
                  }
                />
                <p className="text-text-secondary text-xs mt-1">
                  Análisis de vídeo otorgados por compra (1, pack de 4…).
                </p>
              </div>
              <div>
                <label className="label">Validez (días)</label>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={form.validity_days}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, validity_days: Math.max(1, +e.target.value) }))
                  }
                />
                <p className="text-text-secondary text-xs mt-1">Caducidad del crédito.</p>
              </div>
            </div>
          )}
          {form.fulfillment_type === 'bundle' && editandoLegado && (
            <>
              <div>
                <label className="label">Validez (días) de los créditos</label>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={form.validity_days}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, validity_days: Math.max(1, +e.target.value) }))
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Vídeo análisis (cantidad)</label>
                  <input
                    type="number"
                    min={0}
                    className="input"
                    data-testid="bundle-video-count"
                    value={form.bundle_video_count}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, bundle_video_count: Math.max(0, +e.target.value) }))
                    }
                  />
                </div>
                <div>
                  <label className="label">Sesiones individuales (cantidad)</label>
                  <input
                    type="number"
                    min={0}
                    className="input"
                    data-testid="bundle-session-count"
                    value={form.bundle_session_count}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        bundle_session_count: Math.max(0, +e.target.value),
                      }))
                    }
                  />
                </div>
                <p className="text-text-secondary text-xs col-span-2">
                  El pack concede ambos créditos al comprarlo. Pon 0 en lo que no incluya (al menos
                  uno debe ser mayor que 0).
                </p>
              </div>
              <p
                className="text-xs text-text-secondary bg-bg-elevated rounded p-2"
                data-testid="bundle-legado-nota"
              >
                Combinado del formato antiguo. Se sigue editando así y no se puede convertir: sus
                créditos sueltos no corresponden a ningún producto ni bono del catálogo.
              </p>
            </>
          )}

          {form.fulfillment_type === 'bundle' && !editandoLegado && (
            <div className="space-y-3" data-testid="combinado-v2">
              <div>
                <label className="label">Qué incluye</label>
                <p className="text-text-secondary text-xs mb-2">
                  Comprar el combinado es como comprar estos productos por separado. Lo único que
                  cambia es el precio, que lo pones tú arriba.
                </p>
              </div>

              {itemsV2.map((it, i) => (
                <div key={i} className="flex gap-2 items-start" data-testid={`componente-${i}`}>
                  <select
                    className="input flex-1"
                    data-testid={`componente-ref-${i}`}
                    value={it.id ? `${it.kind}:${it.id}` : ''}
                    onChange={(e) => {
                      const [kind, id] = e.target.value.split(':');
                      setItemsV2((prev) =>
                        prev.map((x, j) =>
                          j === i
                            ? {
                                ...x,
                                kind: kind === 'session_pack' ? 'session_pack' : 'product',
                                id: id ?? '',
                              }
                            : x
                        )
                      );
                    }}
                  >
                    <option value="">— Elige un producto o bono —</option>
                    <optgroup label="Productos">
                      {productosComponibles.map((p) => (
                        <option key={p.id} value={`product:${p.id}`}>
                          {p.name} · {(p.price_cents / 100).toFixed(2)}€
                          {p.active ? '' : ' (inactivo)'}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Bonos de sesiones">
                      {bonos.map((b) => (
                        <option key={b.id} value={`session_pack:${b.id}`}>
                          {b.name} · {(b.price_cents / 100).toFixed(2)}€
                          {b.active ? '' : ' (inactivo)'}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <input
                    type="number"
                    min={1}
                    className="input w-24"
                    data-testid={`componente-cantidad-${i}`}
                    value={it.quantity}
                    onChange={(e) =>
                      setItemsV2((prev) =>
                        prev.map((x, j) =>
                          j === i ? { ...x, quantity: Math.max(1, +e.target.value) } : x
                        )
                      )
                    }
                  />
                  <button
                    type="button"
                    className="text-text-secondary hover:text-error transition-colors mt-2"
                    aria-label="Quitar componente"
                    data-testid={`quitar-componente-${i}`}
                    onClick={() => setItemsV2((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="btn-ghost text-xs flex items-center gap-1.5"
                data-testid="anadir-componente"
                onClick={() =>
                  setItemsV2((prev) => [...prev, { kind: 'product', id: '', quantity: 1 }])
                }
              >
                <Plus size={14} /> Añadir componente
              </button>

              {/* Ayuda para poner el precio, NO un dato. Se calcula al vuelo y
                  no se guarda en ningún sitio: el precio del combinado es el de
                  la venta, y si esta suma se persistiera la contabilidad podría
                  contarla dos veces. */}
              {itemsV2.length > 0 && (
                <p className="text-text-secondary text-xs" data-testid="suma-de-las-partes">
                  Suma de las partes: {(sumaDeLasPartes / 100).toFixed(2)}€
                  {sumaDeLasPartes > form.price_cents && (
                    <> · ahorro {((sumaDeLasPartes - form.price_cents) / 100).toFixed(2)}€</>
                  )}
                  {sumaDeLasPartes > 0 && sumaDeLasPartes < form.price_cents && (
                    <> · ⚠️ el combinado cuesta MÁS que comprarlo suelto</>
                  )}
                </p>
              )}

              {!editTarget && (
                <p
                  className="text-xs text-text-secondary bg-bg-elevated rounded p-2"
                  data-testid="combinado-nace-apagado"
                >
                  Se guardará <strong>desactivado</strong>. Un combinado a la venta antes de que el
                  cobro sepa repartirlo se cobraría entero sin conceder nada. Se activa cuando esa
                  parte esté hecha y probada.
                </p>
              )}

              {componentesInactivos.length > 0 && (
                <p className="text-warning text-xs" data-testid="aviso-inactivos">
                  ⚠️ {componentesInactivos.join(', ')}{' '}
                  {componentesInactivos.length === 1 ? 'está' : 'están'} inactivo
                  {componentesInactivos.length === 1 ? '' : 's'}: el combinado se puede guardar,
                  pero revísalo antes de activarlo.
                </p>
              )}
            </div>
          )}
          {form.fulfillment_type === 'gym_tecnif_subscription' && (
            <>
              <p
                className="text-xs text-text-secondary bg-bg-elevated rounded p-2"
                data-testid="gym-tecnif-note"
              >
                Bundle Preparación Física + Tecnificación: al comprarlo concede N tokens de
                suscripción mensual (el alumno los activa mes a mes, desde el mes siguiente) + 1
                acceso Preparación Física que se activa al asignarle entrenador. El precio es del
                tramo completo.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Meses de tecnificación</label>
                  <input
                    type="number"
                    min={0}
                    className="input"
                    data-testid="bundle-gym-months"
                    value={form.gym_tecnif_months}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, gym_tecnif_months: Math.max(0, +e.target.value) }))
                    }
                  />
                  <p className="text-text-secondary text-xs mt-1">
                    Nº de tokens de suscripción mensual (1, 3, 12…).
                  </p>
                </div>
                <div>
                  <label className="label">Validez acceso Prep. Fís. (días)</label>
                  <input
                    type="number"
                    min={0}
                    className="input"
                    data-testid="bundle-gym-validity"
                    value={form.gym_tecnif_validity_days}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        gym_tecnif_validity_days: Math.max(0, +e.target.value),
                      }))
                    }
                  />
                  <p className="text-text-secondary text-xs mt-1">
                    Días de acceso Preparación Física desde la asignación de entrenador.
                  </p>
                </div>
              </div>
            </>
          )}
          {saveError && <p className="text-error text-xs bg-error/10 rounded p-2">{saveError}</p>}
          {form.fulfillment_type === 'physical_stock' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Categoría</label>
                  <select
                    className="input"
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {Object.entries(CAT_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Stock</label>
                  {editTarget ? (
                    <p
                      className="input bg-bg-elevated text-text-secondary"
                      data-testid="stock-espejo"
                    >
                      {form.stock}
                    </p>
                  ) : (
                    <input
                      type="number"
                      min={0}
                      className="input"
                      data-testid="stock-input"
                      value={form.stock}
                      onChange={(e) => setForm((f) => ({ ...f, stock: +e.target.value }))}
                    />
                  )}
                </div>
              </div>
              {/* Existencias y variantes se llevan en /inventario desde la mig
                  214. Aquí solo se DECLARAN al crear el producto: al editarlo
                  se enseñan, no se tocan — quitar una talla desde aquí no
                  borraría su variante (tiene stock e historial) y el espejo la
                  devolvería, que es lo peor de los dos mundos. */}
              {editTarget ? (
                <div
                  className="rounded-lg border border-bg-border bg-bg-elevated p-4 space-y-2"
                  data-testid="tienda-variantes-espejo"
                >
                  <p className="text-text-secondary text-sm font-inter">
                    Tallas: <span className="text-white">{form.sizes || '—'}</span>
                  </p>
                  <p className="text-text-secondary text-sm font-inter">
                    Colores: <span className="text-white">{form.colors || '—'}</span>
                  </p>
                  <p className="text-text-secondary text-xs font-inter">
                    Las existencias y las variantes se gestionan en{' '}
                    <Link to="/inventario" className="text-gold-primary hover:underline">
                      Inventario
                    </Link>
                    , donde cada cambio deja motivo.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Tallas (separadas por coma)</label>
                    <input
                      className="input"
                      placeholder="XS,S,M,L,XL,XXL"
                      value={form.sizes}
                      onChange={(e) => setForm((f) => ({ ...f, sizes: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="label">Colores (separados por coma)</label>
                    <input
                      className="input"
                      placeholder="Negro/Dorado, Blanco/Dorado"
                      value={form.colors}
                      onChange={(e) => setForm((f) => ({ ...f, colors: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {/* Imágenes */}
          <div>
            <label className="label">Imágenes</label>
            {uploadError && (
              <p className="text-error text-xs bg-error/10 rounded p-2 mb-2">{uploadError}</p>
            )}
            <div className="flex flex-wrap gap-2 mb-2">
              {images.map((url, i) => (
                <div key={i} className="relative w-20 h-20">
                  <img
                    src={url}
                    alt=""
                    className="w-full h-full object-cover rounded border border-bg-border"
                  />
                  <button
                    onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-error rounded-full flex items-center justify-center"
                  >
                    <Trash2 size={10} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-ghost flex items-center gap-2 text-sm"
            >
              <Image size={14} /> {uploading ? 'Subiendo...' : 'Subir imágenes'}
            </button>
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={() => setModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} className="btn-gold">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
